/**
 * Viewport culling geometry: the pure tile-window the camera can currently see.
 *
 * With the map extended to 182x183 the scene holds tens of thousands of land and
 * static sprites, but only the few hundred under the camera at a typical zoom are
 * actually on screen. {@link visibleTileBounds} computes that on-screen tile
 * window so the renderer can prune everything else from the per-frame depth sort,
 * from the sortable world container Pixi re-sorts each frame, and from the GPU
 * draw set - shrinking work that used to scale with the *whole* map down to work
 * that scales with the *viewport*.
 *
 * It imports only the projection constant, reads no clock and no Pixi, so it is
 * fully headless-testable and stays the single source of truth for the window
 * geometry the renderer and its tests share.
 */

import { TILE_HALF, Z_STEP } from '../sim/projection';

/** An inclusive tile-index window: every tile with `minX <= x <= maxX` and so on. */
export interface TileBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * The rectangle of tiles the camera can see, in tile-index space, expanded by
 * `margin` whole tiles on every side.
 *
 * Derivation: the visible canvas spans `screenWidth x screenHeight` px centred on
 * the camera-target tile. The world container is scaled by `zoom`, so in *world*
 * pixels the visible half-extents are `(screenWidth / 2) / zoom` horizontally and
 * `(screenHeight / 2) / zoom` vertically. We invert the dimetric projection at the
 * four screen corners relative to the camera to get their tile offsets - the same
 * inverse `screenToGroundTile` uses: a screen offset `(sx, sy)` from centre is a
 * tile offset `dx = (sx + sy) / (2 * TILE_HALF)`, `dy = (sy - sx) / (2 * TILE_HALF)`.
 * The bounding box of the four corner offsets, added to the camera tile, is the
 * visible window before the margin.
 *
 * The `margin` covers two things the ground-plane inverse alone misses: tall
 * statics (a static at `z=20` draws `20 * Z_STEP = 80px ~= 3.6` tiles up the
 * screen, so its foot tile can sit just outside the window while its art is still
 * visible) and a little safety against rounding. The window is then floored/ceiled
 * out to whole tiles so a partially-visible edge tile is kept, never clipped.
 *
 * Pure: depends only on its arguments and the projection constant.
 */
export function visibleTileBounds(
  camera: { x: number; y: number },
  screenWidth: number,
  screenHeight: number,
  zoom: number,
  margin: number,
): TileBounds {
  // Visible half-extents in world pixels (the zoom divides out the camera scale).
  const halfW = screenWidth / 2 / zoom;
  const halfH = screenHeight / 2 / zoom;

  // Invert the projection at the four screen corners to tile offsets from centre,
  // then take their bounding box. The dimetric inverse is linear, so the extreme
  // tile offsets fall at the screen corners.
  const corners: Array<[number, number]> = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [-halfW, halfH],
    [halfW, halfH],
  ];
  let minDx = Infinity;
  let maxDx = -Infinity;
  let minDy = Infinity;
  let maxDy = -Infinity;
  for (const [sx, sy] of corners) {
    const dx = (sx + sy) / (2 * TILE_HALF);
    const dy = (sy - sx) / (2 * TILE_HALF);
    if (dx < minDx) minDx = dx;
    if (dx > maxDx) maxDx = dx;
    if (dy < minDy) minDy = dy;
    if (dy > maxDy) maxDy = dy;
  }

  return {
    minX: Math.floor(camera.x + minDx - margin),
    maxX: Math.ceil(camera.x + maxDx + margin),
    minY: Math.floor(camera.y + minDy - margin),
    maxY: Math.ceil(camera.y + maxDy + margin),
  };
}

/**
 * The cull margin a map with terrain height needs (terraced land, PRD #536
 * Slice C): the base margin (tall statics + rounding, see the renderer's
 * `CULL_MARGIN`) widened for the map's tallest/deepest land. A tile at
 * elevation `z` draws `z * Z_STEP` px up the screen (a pit draws down), so its
 * foot tile can sit up to `|z| * Z_STEP / (2 * TILE_HALF)` tiles outside the
 * flat-ground window while its art is still visible; without this slack a tall
 * hill's tiles (and the statics/mobiles standing on them) pop in and out at
 * the screen edges. Flat map (`maxAbsLandZ` 0) = exactly the base margin.
 */
export function terrainCullMargin(baseMargin: number, maxAbsLandZ: number): number {
  return baseMargin + Math.ceil((maxAbsLandZ * Z_STEP) / (2 * TILE_HALF));
}

/** Whether a tile `(x, y)` lies inside (inclusive) the given window. */
export function inBounds(bounds: TileBounds, x: number, y: number): boolean {
  return x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY;
}
